import {useState} from 'react';
import {useCalculationGoldListQuery} from './calculation-gold-list-query';
import {EvListParams} from '../../hooks/list/ev-list.models';
import {useEvListFilterHook} from '../../hooks/list/ev-list-hook';
import {useForm} from '@tanstack/react-form';
import {EvNavigationButtons} from '../../pagination/ev-navigation-buttons';
import {EvLoadingSpinner} from '../../components/loading/ev-loading-spinner';
import {clsx} from 'clsx';
import {WhiteTableGrid} from '../../tables/white-table-grid';
import {EvLoadingTanStackQuery} from '../../components/loading/ev-loading-tan-stack-query';
import {EvDatePickerField} from '../../form/EvDatepickerField';
import {CalculationGoldElement} from './calculation-gold-element';
import {CalculationGoldListElement, CalculationGoldListQueryForm} from './calculation-gold.models';
import {CalculationGoldForm} from './calculation-gold-form';
import {CalculationGoldImportUmicoreForm} from './calculation-gold-import-umicore-form';

function goldListConfig(stateName: string): EvListParams {
  return {
    stateName,
    config: {
      types: {
        date: 'string',
        months: 'int',
        page: 'int',
        sort: 'string',
        order: 'string',
      },
    },
  };
}

interface Props {
  months?: number | null;
  stateName: string;
}

export function CalculationGoldList(props: Props) {
  const filters = useEvListFilterHook<CalculationGoldListQueryForm>(goldListConfig(props.stateName));
  const [state, setState] = useState<{ selected: CalculationGoldListElement | null, now: Date }>({
    selected: null,
    now: new Date(),
  });
  const filterForm = useForm({
    defaultValues: filters.params,
    onSubmit: () => {
    },
    listeners: {
      onChange: ({formApi}) => {
        filters.formChanged(formApi.state.values);
      },
      onChangeDebounceMs: 750,
    },
  });

  const query = useCalculationGoldListQuery({...filters.params, months: props.months ?? null});
  return <div className="row">
    <div className="col-lg-6">
      <form onSubmit={async (e) => {
        e.preventDefault();
        await filterForm.handleSubmit();
      }}>
        <div className="min-h-[80dvh]">
          <WhiteTableGrid gridColsClass="grid-cols-30-25-5-25-5-5-5" colSpanClass="col-span-7">
            {{
              headerRows: [[
                <div key="date" className="p-2">Datum</div>,
                <div key="price" className="p-2 text-right">Preis (€ / g)</div>,
                <div key="editButton" className="p-2"/>,
                <div key="priceNew" className="p-2 text-right">Preis Neu (€ / g)</div>,
                <div key="validationButton" className="p-2"/>,
                <div key="validationPriceButton" className="p-2"/>,
                <div key="deleteButton" className="p-2"/>,
              ]],
              filterRows: [[
                <div key="date" className="p-2">
                  <filterForm.Field name="date">
                    {(field) => <EvDatePickerField field={field} formStyle="sm"/>}
                  </filterForm.Field>
                </div>,
                <div key="price" className="p-2 text-right"/>,
                <div key="editButton" className="p-2"/>,
                <div key="priceNew" className="p-2 text-right"/>,
                <div key="validationButton" className="p-2"/>,
                <div key="validationPriceButton" className="p-2"/>,
                <div key="deleteButton" className="p-2"/>,
              ]],
              body: ([gridColsClass, colSpanClass, ...classNames]) =>
                <EvLoadingTanStackQuery query={query}>
                  {{
                    success: (data) =>
                      data.objects.map(p =>
                        <CalculationGoldElement key={p.id}
                                                selected={() => setState({
                                                  ...state,
                                                  selected: state.selected?.id === p.id ? null : p,
                                                })}
                                                element={p}
                                                className={clsx(gridColsClass, colSpanClass, classNames)}
                                                colSpanClass={colSpanClass}/>),
                    error:
                      <div className={clsx(colSpanClass, classNames, 'text-center')}>
                        Fehler beim laden der Liste
                      </div>,
                    loading:
                      <div className={clsx(gridColsClass, colSpanClass, classNames)}>
                        <EvLoadingSpinner/>
                      </div>,
                  }}
                </EvLoadingTanStackQuery>,
            }}
          </WhiteTableGrid>
        </div>

        <EvNavigationButtons page={(filters.params.page ?? 1) as number}
                             pagesTotal={query.data?.pages ?? 1}
                             setPage={filters.pageChanged}/>
      </form>
    </div>
    <div className="col-lg-4">
      <CalculationGoldForm key={(state.selected?.id ?? -1)}
                           update={state.selected}
                           months={props.months}
                           deselected={() => setState({...state, selected: null})}>
        <h3>{state.selected ? `Preis für den ${state.selected.date} bearbeiten` : 'Preis für neues Datum setzen'}</h3>
      </CalculationGoldForm>
      <CalculationGoldImportUmicoreForm now={state.now} />
    </div>
  </div>;
}
